import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { api } from "../../store/queries";
import {
  useDismissClaimBulkActionMutation,
  useGetClaimBulkActionsQuery,
} from "../../store/queries/claims";
import { ClaimBulkActionStatus, IClaimBulkAction } from "../../types/claim";
import { claimCount, useBulkActionToast } from "./bulkActionShared";

interface IClaimBulkActionsBannerProps {
  accountId: string;
}

/** How often the banner polls while any bulk action is queued or running. */
export const CLAIM_BULK_ACTIONS_POLL_MS = 5000;

const NO_ACTIONS: IClaimBulkAction[] = [];

const isActive = (status: ClaimBulkActionStatus | undefined) =>
  status === "queued" || status === "running";
const isFinished = (status: ClaimBulkActionStatus | undefined) =>
  status === "completed" || status === "failed";

const ACTION_NOUN: Record<IClaimBulkAction["actionType"], string> = {
  reprocess: "Reprocess",
  change_payer: "Payer update",
  ai_analysis: "AI analysis",
};

const ACTION_ICON: Record<IClaimBulkAction["actionType"], string> = {
  reprocess: "pi pi-refresh",
  change_payer: "pi pi-pencil",
  ai_analysis: "pi pi-sparkles",
};

/** "Reprocessing 312 claims", "Updating the matched payer on 40 claims → Aetna", … */
export const bulkActionLabel = (action: IClaimBulkAction): string => {
  const claims = claimCount(action.totalCount);
  if (action.actionType === "reprocess") return `Reprocessing ${claims}`;
  if (action.actionType === "ai_analysis")
    return `Running AI analysis on ${claims}`;
  const payer =
    action.payerId === null
      ? "No Payer Match"
      : action.payerName ?? `payer #${action.payerId}`;
  return `Updating the matched payer on ${claims} → ${payer}`;
};

/** The line under the progress bar. */
export const bulkActionStatusLine = (action: IClaimBulkAction): string => {
  switch (action.status) {
    case "queued":
      return `Queued — waiting for the EDI worker${
        action.queuedAhead > 0
          ? ` behind ${action.queuedAhead} other job${
              action.queuedAhead === 1 ? "" : "s"
            }`
          : ""
      }`;
    case "running":
      return `${action.processedCount.toLocaleString(
        "en-US"
      )} of ${action.totalCount.toLocaleString("en-US")} done`;
    case "completed":
      return `Done: ${action.successCount.toLocaleString(
        "en-US"
      )} succeeded, ${action.failureCount.toLocaleString("en-US")} failed`;
    default:
      return `Failed: ${action.error ?? "the job stopped before finishing"}`;
  }
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const Row = styled.div`
  padding: 0.625rem 0.875rem;
  background-color: #f5f9ff;
  border: 1px solid #c5d9f1;
  border-radius: 6px;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e3a5f;
`;

const Grow = styled.span`
  flex: 1;
`;

const StatusLine = styled.div<{ $tone: "neutral" | "good" | "warn" | "bad" }>`
  margin-top: 0.375rem;
  font-size: 0.875rem;
  color: ${({ $tone }) =>
    ({
      neutral: "#37474f",
      good: "#2e7d32",
      warn: "#e65100",
      bad: "#c62828",
    }[$tone])};
`;

const FailureToggle = styled.button`
  background: none;
  border: none;
  color: #1565c0;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
`;

const FailureList = styled.ul`
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
  font-size: 0.8125rem;
  color: #37474f;
`;

const toneOf = (action: IClaimBulkAction) => {
  if (action.status === "failed") return "bad" as const;
  if (action.status === "completed")
    return action.failureCount === 0 ? ("good" as const) : ("warn" as const);
  return "neutral" as const;
};

function BulkActionRow({
  action,
  onDismiss,
}: {
  action: IClaimBulkAction;
  onDismiss: (id: string) => void;
}) {
  const [showFailures, setShowFailures] = useState(false);
  const percent =
    action.totalCount > 0
      ? Math.round((action.processedCount / action.totalCount) * 100)
      : 0;

  return (
    <Row data-testid={`claim-bulk-action-${action.id}`}>
      <RowHeader>
        <i className={ACTION_ICON[action.actionType]} aria-hidden />
        <Grow>{bulkActionLabel(action)}</Grow>
        {isFinished(action.status) && (
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-rounded p-button-sm"
            aria-label="Dismiss"
            onClick={() => onDismiss(action.id)}
          />
        )}
      </RowHeader>
      <ProgressBar
        value={percent}
        showValue={false}
        style={{ height: "6px", marginTop: "0.375rem" }}
      />
      <StatusLine $tone={toneOf(action)}>
        {bulkActionStatusLine(action)}
      </StatusLine>
      {action.failures.length > 0 && (
        <>
          <FailureToggle
            type="button"
            onClick={() => setShowFailures((open) => !open)}
          >
            {showFailures ? "Hide failed claims" : "Show failed claims"}
          </FailureToggle>
          {showFailures && (
            <FailureList>
              {action.failures.map((failure) => (
                <li key={failure.claimId}>
                  {failure.claimNumber ?? failure.claimId}
                  {failure.error ? ` — ${failure.error}` : ""}
                </li>
              ))}
            </FailureList>
          )}
        </>
      )}
    </Row>
  );
}

/**
 * CLE-3035: the claims list's background bulk actions (reprocess, matched-payer
 * change, AI analysis) — one row per action with a progress bar, polled every
 * 5 s while any is queued or running and not at all once they have settled
 * (the useEdiSplits pattern). When an action finishes it toasts once and
 * refreshes the claims list, so the re-priced money shows without a reload.
 * Finished actions stay for 24 hours or until dismissed.
 */
export function ClaimBulkActionsBanner({
  accountId,
}: IClaimBulkActionsBannerProps) {
  const dispatch = useDispatch();
  const { localToast, show } = useBulkActionToast();
  const [polling, setPolling] = useState(false);
  // currentData, not data: after an account switch `data` still holds the previous account's actions.
  const { currentData: data } = useGetClaimBulkActionsQuery(
    { accountId },
    {
      pollingInterval: polling ? CLAIM_BULK_ACTIONS_POLL_MS : 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const [dismiss] = useDismissClaimBulkActionMutation();
  const actions = data ?? NO_ACTIONS;
  const anyActive = actions.some((action) => isActive(action.status));

  // The polling decision is read from the data the query returned, so it can only follow it.
  useEffect(() => {
    setPolling(anyActive);
  }, [anyActive]);

  // Toast + refresh the list on the FIRST transition of an action into a finished state. The first
  // response only records what is already there (an action that finished before the page opened is
  // not news); after that, an action seen finished for the first time — including one that queued
  // and finished between two polls — is announced once.
  // Keyed by account: switching account starts over, as the new account's finished actions are not news either.
  const prevStatusesRef = useRef<{
    accountId: string;
    statuses: Map<string, ClaimBulkActionStatus>;
  } | null>(null);
  useEffect(() => {
    if (!data) return;
    const prev =
      prevStatusesRef.current?.accountId === accountId
        ? prevStatusesRef.current.statuses
        : null;
    const next = new Map<string, ClaimBulkActionStatus>();
    let anyFinished = false;
    data.forEach((action) => {
      next.set(action.id, action.status);
      if (
        !prev ||
        !isFinished(action.status) ||
        isFinished(prev.get(action.id))
      )
        return;
      anyFinished = true;
      const noun = ACTION_NOUN[action.actionType];
      if (action.status === "completed") {
        show({
          severity: action.failureCount === 0 ? "success" : "warn",
          summary: `${noun} finished`,
          detail: `${noun} finished: ${action.successCount.toLocaleString(
            "en-US"
          )} succeeded, ${action.failureCount.toLocaleString("en-US")} failed.`,
          life: 8000,
        });
      } else {
        show({
          severity: "error",
          summary: `${noun} failed`,
          detail: action.error ?? "The job stopped before finishing.",
          life: 10000,
        });
      }
    });
    prevStatusesRef.current = { accountId, statuses: next };
    // "PrecalculatedAllowables" too, as the synchronous mutations did before CLE-3035: a reprocess or payer
    // change re-prices, and the claim modal's Expected Amounts tab reads the precalc.
    if (anyFinished)
      dispatch(api.util.invalidateTags(["Claims", "PrecalculatedAllowables"]));
  }, [data, accountId, dispatch, show]);

  return (
    <>
      <Toast ref={localToast} />
      {actions.length > 0 && (
        <Wrapper data-testid="claim-bulk-actions-banner">
          {actions.map((action) => (
            <BulkActionRow
              key={action.id}
              action={action}
              onDismiss={(id) => {
                dismiss({ id });
              }}
            />
          ))}
        </Wrapper>
      )}
    </>
  );
}
